import { combineReducers, createNextState, isDraft } from '@reduxjs/toolkit';
import { isDraftable } from 'immer';
import type { Reducer } from './rtk-types';
import { storeManager } from './storeManager';
import { patchStoreReducer } from './patchStoreReducer';
import type { GetStoreFn, InjectReducerFn } from './types';

const emptyReducer = <T>(state: T) => state;

/**
 * Функция для заморозки состояния стора
 * @internal
 */
function freezeDraftable<T>(val: T) {
    if (Object.isFrozen(val) || isDraft(val) || !isDraftable(val)) {
        return val;
    }

    return createNextState(val, () => {});
}

/**
 * Функция оборачивает редьюсер, чтобы любое изменение стейта замораживало его
 * @internal
 */
function freezeReducer(reducer: Reducer) {
    return (...args: Parameters<Reducer>) => {
        const nextState = reducer(...args);

        return freezeDraftable(nextState);
    };
}

export function createDynamicReducer(moduleName: string, getStore: GetStoreFn) {
    /**
     * Список редьюсеров, которые были подключены к стору в рантайме.
     */
    const dynamicReducers: Record<string, Reducer> = {};

    /**
     * Добавляет редьюсер в модульный стор.
     *
     * @param key – Ключ, по которому должен быть добавлен редьюсер.
     * @param reducer – Редьюсер, который нужно добавить.
     */
    const injectReducer: InjectReducerFn = (key, reducer, options = {}) => {
        if (key in dynamicReducers && !options.overwrite) {
            throw new Error(`Reducer "${key}" already exist.`);
        }

        dynamicReducers[key] = reducer;

        const hasStore = storeManager.hasStore(moduleName);
        if (hasStore) {
            getStore().replaceReducer(getDynamicReducer());
        }
    };

    /**
     * Создает объединенный редьюсер из массива зарегистрированных модульных редьюсеров
     */
    const getDynamicReducer = () => {
        if (Object.keys(dynamicReducers).length === 0) {
            return freezeReducer(emptyReducer);
        }

        return freezeReducer(patchStoreReducer(combineReducers(dynamicReducers)));
    };

    return { getDynamicReducer, injectReducer };
}
